import React, { useMemo } from 'react';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import InstructionsMarkdownEditorFormik from '~/components/Admin/AiExperts/InstructionsMarkdownEditorFormik';
import CardDialog from '~/components/CardDialog';
import AlertBanner from '~/components/core/AlertBanner';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import Text from '~/components/core/TextComponents/Text';
import TextFieldFormikUntyped from '~/components/TextFieldFormik';
import { AiExpertScopeConfigurationInfoStructuredOutputComponent as StructuredOutputComponent } from '~/server_shared/openapi/data-contracts';

import copies from '../copies';
import type {
  AiExpertScopeConfigurationInfo,
  AiExpertScopeValues,
  ExpertDialogFormValues,
  OrganizationAiExpertInfo,
} from '../types';
import { EXPERT_RUN_MODES, expertRunMode, runModeFlags } from '../types';

interface EditExpertDialogProps {
  expert: OrganizationAiExpertInfo;
  /** This scope's stored row, or `undefined` when the expert has never been configured here. */
  row?: AiExpertScopeConfigurationInfo;
  /** Whether this scope runs the conductor; without it the conductor mode has nothing to fire it. */
  isConductorEnabledForScope: boolean;
  onClose: () => void;
  /** Persists the whole row and resolves to the refreshed rows, or `undefined` if the refresh failed. */
  onSave: (values: AiExpertScopeValues) => Promise<unknown>;
}

interface TextFieldFormikProps {
  id: string;
  label?: string;
  fullWidth?: boolean;
  type?: string;
}
const TextFieldFormik = TextFieldFormikUntyped as React.ComponentType<TextFieldFormikProps>;

/**
 * Whether the expert itself can run in `mode`, whatever this scope does; Off always can.
 *
 * The AI service runs an expert only under the triggers its definition allows, so any other mode
 * would be stored and never run -- the server refuses to save one.
 *
 * Only an explicit `false` rules a mode out: a response without the field (a backend that predates
 * it, while a deploy's frontend and backend promotions race) carries no run-mode information, which
 * the server reads as unconstrained.
 */
const isRunModeSupported = (expert: OrganizationAiExpertInfo, mode: string | undefined): boolean =>
  !(mode === EXPERT_RUN_MODES.CONDUCTOR && expert.conductor_supported === false) &&
  !(mode === EXPERT_RUN_MODES.STANDALONE && expert.standalone_supported === false);

const buildValidationSchema = (expert: OrganizationAiExpertInfo) =>
  Yup.object().shape({
    run_mode: Yup.string()
      .oneOf(Object.values(EXPERT_RUN_MODES))
      .required()
      // The option is disabled, but a row stored before the check existed can still open on it, and
      // the server refuses to save it again in that mode.
      .test('run-mode-supported', copies.experts.dialog.runModeUnsupported, (mode) => isRunModeSupported(expert, mode)),
    instructions: Yup.string().nullable(),
    // Checked here as well as on the server, because the server's rejection is the only thing that
    // used to catch it and the dialog surfaces a 4xx as the generic "unexpected error" — which tells
    // an operator nothing about which field is wrong or what a valid value looks like.
    //
    // Scoped to the standalone branch on purpose: the input is not rendered in the other two modes,
    // so validating a stale value there would block Save on an error message nothing displays.
    // Blank stays valid in every mode — it means "no floor", not "unfilled".
    standalone_confidence_threshold: Yup.mixed().when('run_mode', {
      is: EXPERT_RUN_MODES.STANDALONE,
      then: Yup.mixed().test(
        'threshold-between-zero-and-one',
        copies.experts.dialog.thresholdError,
        (value) => value === '' || value === null || value === undefined || (Number(value) >= 0 && Number(value) <= 1)
      ),
      otherwise: Yup.mixed(),
    }),
  });

/**
 * One radio option's label: the mode, and under it what the mode does.
 *
 * Composed into the label rather than passed as the group's `note`, which renders the text as a
 * sibling *after* the whole control -- so it starts under the radio circle instead of under the
 * title, and the radio aligns against the title alone. Inside the label both lines share the
 * control's own alignment, which is the mock's layout.
 */
const optionLabel = (title: string, helper: string): React.ReactNode => (
  <span className="flex flex-col gap-2 py-4">
    <Text variant={Text.VARIANTS.SM}>{title}</Text>
    <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
      {helper}
    </Text>
  </span>
);

/**
 * The run mode, and the threshold that belongs to one of it.
 *
 * One choice rather than a switch per mode: an expert runs with the conductor, or on its own
 * trigger, or not at all. A mode that cannot run stays visible but disabled -- the conductor's when
 * the scope's conductor is off, and either one the expert itself does not support -- because an
 * operator who cannot see an option cannot tell why the expert will not run that way.
 */
const ExpertRunModeFields: React.FC<{ expert: OrganizationAiExpertInfo; isConductorEnabledForScope: boolean }> = ({
  expert,
  isConductorEnabledForScope,
}) => {
  const { values } = useFormikContext<ExpertDialogFormValues>();
  const { dialog } = copies.experts;
  const isConductorSupported = isRunModeSupported(expert, EXPERT_RUN_MODES.CONDUCTOR);
  const isStandaloneSupported = isRunModeSupported(expert, EXPERT_RUN_MODES.STANDALONE);

  const options = [
    { optionValue: EXPERT_RUN_MODES.OFF, text: optionLabel(dialog.modeOffLabel, dialog.modeOffHelper) },
    {
      optionValue: EXPERT_RUN_MODES.CONDUCTOR,
      text: optionLabel(dialog.conductorLabel, dialog.conductorHelper),
      // The conductor is what does the consulting, so its own switch gates this option as well.
      disabled: !isConductorSupported || !isConductorEnabledForScope,
    },
    {
      optionValue: EXPERT_RUN_MODES.STANDALONE,
      text: optionLabel(dialog.standaloneLabel, dialog.standaloneHelper),
      disabled: !isStandaloneSupported,
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      <RadioButtonGroupFormik
        id="run_mode"
        label={dialog.runModeLabel}
        options={options}
        direction="column"
        buttonVariant="default"
        // The label is two lines, and MUI centres the control against the whole of it; the radio
        // belongs beside the title. Tailwind emits `body .items-start`, specificity (0,1,1), so it
        // beats MUI's single-class `align-items: center` regardless of sheet order.
        btnLabelClassName="items-start"
      />
      {/* One reason per disabled option. The expert's own is the one to give when both apply:
          turning the scope's conductor on would not make the option available. */}
      {!isConductorSupported ? (
        <AlertBanner alertType="info" note={dialog.conductorUnsupported} withIcon />
      ) : !isConductorEnabledForScope ? (
        <AlertBanner alertType="info" note={dialog.conductorScopeOff} withIcon />
      ) : null}
      {!isStandaloneSupported ? <AlertBanner alertType="info" note={dialog.standaloneUnsupported} withIcon /> : null}
      {values.run_mode === EXPERT_RUN_MODES.STANDALONE ? (
        <div className="flex flex-col gap-4">
          <TextFieldFormik id="standalone_confidence_threshold" label={dialog.thresholdLabel} type="number" fullWidth />
          <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {dialog.thresholdHelper}
          </Text>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Holds a local draft of one expert's row for one scope, committed on Save.
 *
 * Mounted keyed on the expert by its parent, never reused across experts: the markdown editor
 * seeds its content on mount only.
 */
const EditExpertDialog: React.FC<EditExpertDialogProps> = ({
  expert,
  row,
  isConductorEnabledForScope,
  onClose,
  onSave,
}) => {
  // Formik lists the schema in the dependency arrays behind its enableReinitialize effect.
  const validationSchema = useMemo(() => buildValidationSchema(expert), [expert]);
  const initialValues: ExpertDialogFormValues = {
    run_mode: expertRunMode(row),
    standalone_confidence_threshold: row?.standalone_confidence_threshold ?? '',
    instructions: row?.instructions ?? '',
  };

  const handleSubmit = async (values: ExpertDialogFormValues) => {
    const isStandalone = values.run_mode === EXPERT_RUN_MODES.STANDALONE;
    const saved = await onSave({
      ...runModeFlags(values.run_mode),
      // The server rejects a threshold on a row that is not standalone, so the number goes with
      // the mode rather than lingering as a stale value the next save is refused for.
      standalone_confidence_threshold:
        !isStandalone || values.standalone_confidence_threshold === '' ? null : values.standalone_confidence_threshold,
      instructions: values.instructions || null,
      // Both echoed rather than defaulted. The write replaces the whole row and this dialog has no
      // control for either, so sending a literal would reset whatever the QA Configuration tab set
      // on the same row.
      configured_fields: row?.configured_fields ?? [],
      render_component: row?.render_component ?? StructuredOutputComponent.KeyValue,
    });
    if (saved !== undefined) {
      onClose();
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ isSubmitting, handleSubmit: submitForm }) => (
        <CardDialog
          isDialog
          open
          maxWidth="lg"
          fullWidth
          title={copies.experts.dialog.title(expert.display_name)}
          onClose={onClose}
          preventClose={isSubmitting}
          footerActions={
            <DialogFooterActions
              onClickPrimary={() => submitForm()}
              onClickSecondary={onClose}
              disabled={isSubmitting}
            />
          }
        >
          <div className="flex flex-col gap-20">
            <ExpertRunModeFields expert={expert} isConductorEnabledForScope={isConductorEnabledForScope} />
            <div className="flex flex-col gap-8">
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
                {copies.experts.dialog.instructionsTitle}
              </Text>
              <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                {copies.experts.dialog.instructionsHelper}
              </Text>
              <InstructionsMarkdownEditorFormik id="instructions" />
            </div>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default EditExpertDialog;
